import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Grid,
  Chip,
  Stack,
  Autocomplete,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { API_BASE_URL, API_ENDPOINTS } from '../config/apiClient';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import { ApprovalRequestItem, FileArtifact } from '../types';

interface ItemEstimate {
  documentId: string;
  item: string;
  unit: string;
  remarks?: string;
  quantity: number;
  unitCost: number;
  totalCost: number;
}

export default function RequestApproval () {
  const navigate = useNavigate();
  const { user, handleUnauthorized } = useAuth();
  const [items, setItems] = useState<ApprovalRequestItem[]>([]);
  const [itemEstimates, setItemEstimates] = useState<ItemEstimate[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openNewItemDialog, setOpenNewItemDialog] = useState(false);
  const [selectedItemEstimate, setSelectedItemEstimate] = useState<ItemEstimate | null>(null);
  const [formData, setFormData] = useState({
    itemName: '',
    quantity: '',
    rate: '',
    totalAmount: '',
    comment: '',
  });
  const [newItemData, setNewItemData] = useState({
    item: '',
    unit: '',
    remarks: '',
    quantity: '',
    unitCost: '',
    totalCost: '',
  });
  const [uploadedFiles, setUploadedFiles] = useState<FileArtifact[]>([]);
  const [requestorName, setRequestorName] = useState('');
  const [requestTitle, setRequestTitle] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: string }>({});
  const [totalFilesToUpload, setTotalFilesToUpload] = useState(0);
  const [filesUploaded, setFilesUploaded] = useState(0);
  const [openFileManagerDialog, setOpenFileManagerDialog] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);
  const [editingApprovalItemIndex, setEditingApprovalItemIndex] = useState<number | null>(null);

  // Auto-populate requestor name from logged-in user
  useEffect(() => {
    if (user) {
      setRequestorName(user.email || '');
    }
  }, [user]);

  // Fetch item estimates
  useEffect(() => {
    const fetchItemEstimates = async () => {
      try {
        const token = localStorage.getItem('strapiToken');
        const response = await fetch(
          `${API_BASE_URL}${API_ENDPOINTS.ITEM_ESTIMATES.LIST}?pagination[limit]=1000&fields[0]=item&fields[1]=unit&fields[2]=remarks&fields[3]=quantity&fields[4]=unitCost&fields[5]=totalCost`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          setItemEstimates(data.data);
        } else if (response.status === 401 || response.status === 403) {
          handleUnauthorized();
        }
      } catch (error) {
        console.error('Failed to fetch item estimates:', error);
      }
    };

    fetchItemEstimates();
  }, []);

  const handleAddItem = () => {
    if (!selectedItemEstimate && !formData.itemName) {
      setSubmitError('Please select an item or enter a new item name');
      return;
    }

    if (!formData.quantity || !formData.rate || !formData.totalAmount) {
      setSubmitError('Please fill in quantity, rate, and total amount');
      return;
    }

    const quantity = parseFloat(formData.quantity);
    const rate = parseFloat(formData.rate);
    const totalAmount = parseFloat(formData.totalAmount);

    const itemName = selectedItemEstimate ? selectedItemEstimate.item : formData.itemName;

    const newItem: ApprovalRequestItem = {
      itemName,
      quantity,
      rate,
      totalAmount,
      files: uploadedFiles.length > 0 ? [...uploadedFiles] : undefined,
      itemEstimateId: selectedItemEstimate?.documentId,
      comment: formData.comment || undefined,
    };

    if (editingApprovalItemIndex !== null) {
      // Update existing item
      const updatedItems = [...items];
      updatedItems[editingApprovalItemIndex] = newItem;
      setItems(updatedItems);
    } else {
      // Add new item
      setItems([...items, newItem]);
    }

    setFormData({
      itemName: '',
      quantity: '',
      rate: '',
      totalAmount: '',
      comment: '',
    });
    setSelectedItemEstimate(null);
    setUploadedFiles([]);
    setOpenDialog(false);
    setEditingApprovalItemIndex(null);
    setSubmitError('');
  };

  const handleAddNewItem = async () => {
    if (!newItemData.item || !newItemData.unit || !newItemData.quantity || !newItemData.unitCost || !newItemData.totalCost) {
      setSubmitError('Please fill in all required fields for new item');
      return;
    }

    try {
      const token = localStorage.getItem('strapiToken');
      if (!token) {
        setSubmitError('Authentication token not found');
        return;
      }

      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ITEM_ESTIMATES.CREATE}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          data: {
            item: newItemData.item,
            unit: newItemData.unit,
            remarks: newItemData.remarks,
            quantity: parseFloat(newItemData.quantity),
            unitCost: parseFloat(newItemData.unitCost),
            totalCost: parseFloat(newItemData.totalCost),
            publishedAt: new Date().toISOString(),
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to create item estimate');
      }

      const createdItem = await response.json();
      const newEstimate: ItemEstimate = createdItem.data;
      
      setItemEstimates([...itemEstimates, newEstimate]);
      setSelectedItemEstimate(newEstimate);
      
      // Auto-fill form with the new item
      setFormData({
        itemName: newEstimate.item,
        quantity: newItemData.quantity,
        rate: newItemData.unitCost,
        totalAmount: newItemData.totalCost,
        comment: '',
      });

      setNewItemData({
        item: '',
        unit: '',
        remarks: '',
        quantity: '',
        unitCost: '',
        totalCost: '',
      });
      setOpenNewItemDialog(false);
      setSubmitError('');
    } catch (error) {
      console.error('Error creating item estimate:', error);
      setSubmitError(error instanceof Error ? error.message : 'Failed to create new item');
    }
  };

  const handleDeleteItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleEditItem = (index: number) => {
    const item = items[index];
    setEditingApprovalItemIndex(index);
    
    // Find the selected item estimate if available
    if (item.itemEstimateId) {
      const estimate = itemEstimates.find(est => est.documentId === item.itemEstimateId);
      setSelectedItemEstimate(estimate || null);
    }
    
    // Populate form with item data
    setFormData({
      itemName: item.itemName,
      quantity: item.quantity.toString(),
      rate: item.rate.toString(),
      totalAmount: item.totalAmount.toString(),
      comment: item.comment || '',
    });
    
    // Set uploaded files if they exist
    if (item.files && item.files.length > 0) {
      setUploadedFiles([...item.files]);
    }
    
    setOpenDialog(true);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newFiles: FileArtifact[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      newFiles.push({
        name: file.name,
        size: file.size,
        type: file.type,
        uploadedAt: new Date().toISOString(),
        file: file,
      });
    }
    setUploadedFiles([...uploadedFiles, ...newFiles]);
  };

  const handleRemoveFile = (index: number) => {
    setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
  };

  const handleManageItemFiles = (index: number) => {
    setEditingItemIndex(index);
    setUploadedFiles([...(items[index].files || [])]);
    setOpenFileManagerDialog(true);
  };

  const handleSaveItemFiles = () => {
    if (editingItemIndex !== null) {
      const updatedItems = [...items];
      updatedItems[editingItemIndex].files = uploadedFiles.length > 0 ? uploadedFiles : undefined;
      setItems(updatedItems);
      setOpenFileManagerDialog(false);
      setUploadedFiles([]);
      setEditingItemIndex(null);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleSubmit = async () => {
    if (!requestTitle.trim()) {
      setSubmitError('Please enter a title for the request');
      return;
    }

    if (!requestorName) {
      setSubmitError('Please enter your name');
      return;
    }

    if (items.length === 0) {
      setSubmitError('Please add at least one item');
      return;
    }

    console.log('Submitting items with files:', items);

    // Calculate total files to upload
    const totalFiles = items.reduce((sum, item) => sum + (item.files?.length || 0), 0);
    setTotalFilesToUpload(totalFiles);
    setFilesUploaded(0);
    setUploadProgress({});
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const token = localStorage.getItem('strapiToken');
      if (!token) {
        setSubmitError('Authentication token not found');
        setIsSubmitting(false);
        return;
      }

      // Fetch all users with Approver role
      const usersResponse = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.USERS.GET_APPROVERS}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!usersResponse.ok) {
        throw new Error('Failed to fetch approvers');
      }

      const usersData = await usersResponse.json();
      
      // Build approvals array from approvers
      const approvalsArray = usersData.map((approver: any) => ({
        email: approver.email,
        username: approver.username,
        status: 'Pending'
      }));

      let createdItemIds: string[] = [];
      let approvalId: string | null = null;

      try {
        // Step 1: Create item requests with file uploads
        const itemRequestsPromises = items.map(async (item) => {
          console.log('Processing item:', item);
          
          // Create item request in DRAFT mode (without publishedAt)
          const itemResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ITEM_REQUESTS.CREATE}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              data: {
                quantity: item.quantity,
                unitPrice: item.rate,
                totalPrice: item.totalAmount,
                item_estimate: item.itemEstimateId ? item.itemEstimateId : null,
                comment: item.comment || null,
              }
            })
          });

          if (!itemResponse.ok) {
            const errorData = await itemResponse.json();
            throw new Error(errorData.error?.message || 'Failed to create item request');
          }

          const itemData = await itemResponse.json();
          const itemId = itemData.data.documentId;
          console.log('Created item request (draft):', itemId);

          // Step 1b: Upload files if any exist for this item
          let uploadedFileIds: number[] = [];
          if (item.files && item.files.length > 0) {
            console.log(`Uploading ${item.files.length} files for item ${itemId}`);
            for (const fileArtifact of item.files) {
              if (fileArtifact.file) {
                console.log('Uploading file:', fileArtifact.name);
                
                // Update progress
                setUploadProgress(prev => ({
                  ...prev,
                  [fileArtifact.name]: 'Uploading...'
                }));

                const formData = new FormData();
                formData.append('files', fileArtifact.file);
                formData.append('ref', 'api::itemrequest.itemrequest');
                formData.append('refId', itemId);
                formData.append('field', 'attachment');

                const uploadResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.UPLOAD.FILE}`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                  },
                  body: formData
                });

                if (!uploadResponse.ok) {
                  const errorData = await uploadResponse.json();
                  console.error('Upload error response:', errorData);
                  setUploadProgress(prev => ({
                    ...prev,
                    [fileArtifact.name]: '❌ Upload failed'
                  }));
                  throw new Error(errorData.error?.message || 'Failed to upload file');
                }

                const uploadData = await uploadResponse.json();
                console.log('Upload API Response:', uploadData);
                
                // Extract file ID(s) from response (it's an array)
                if (Array.isArray(uploadData)) {
                  uploadData.forEach((fileInfo: any) => {
                    uploadedFileIds.push(fileInfo.id);
                  });
                } else if (uploadData.id) {
                  uploadedFileIds.push(uploadData.id);
                }
                
                console.log('File uploaded successfully:', fileArtifact.name, 'ID:', uploadedFileIds);
                
                // Update progress to success
                setUploadProgress(prev => ({
                  ...prev,
                  [fileArtifact.name]: '✅ Uploaded'
                }));
                setFilesUploaded(prev => prev + 1);
              }
            }

            // Step 1b-ii: Link uploaded files to itemrequest attachment field
            if (uploadedFileIds.length > 0) {
              console.log('Linking files to itemrequest:', itemId, 'File IDs:', uploadedFileIds);
              const linkResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ITEM_REQUESTS.UPDATE(itemId)}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                  data: {
                    attachment: uploadedFileIds
                  }
                })
              });

              if (!linkResponse.ok) {
                const errorData = await linkResponse.json();
                console.error('Link files error response:', errorData);
                throw new Error(errorData.error?.message || 'Failed to link files to itemrequest');
              }

              const linkData = await linkResponse.json();
              console.log('Files linked successfully:', linkData);
            }
          } else {
            console.log('No files to upload for this item');
          }

          // Step 1c: Publish the itemrequest after files are uploaded
          console.log('Publishing item request:', itemId);
          const publishResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ITEM_REQUESTS.UPDATE(itemId)}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              data: {
                publishedAt: new Date().toISOString(),
              }
            })
          });

          if (!publishResponse.ok) {
            const errorData = await publishResponse.json();
            console.error('Publish error response:', errorData);
            throw new Error(errorData.error?.message || 'Failed to publish item request');
          }

          const publishedData = await publishResponse.json();
          console.log('Item request published:', publishedData);

          return itemId;
        });

        const createdIds = await Promise.all(itemRequestsPromises);
        createdItemIds = createdIds;

        console.log('Created item requests with IDs:', createdItemIds);

        // Step 2: All items created and published successfully, now create approval request
        const approvalResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.APPROVAL_REQUESTS.CREATE}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            data: {
              users_permissions_user: user?.id,
              title: requestTitle,
              approvalStatus: 'Pending',
              approvals: approvalsArray,
              publishedAt: new Date().toISOString(),
              itemrequests: createdItemIds
            }
          })
        });

        if (!approvalResponse.ok) {
          const errorData = await approvalResponse.json();
          throw new Error(errorData.error?.message || 'Failed to create approval request');
        }

        const approvalData = await approvalResponse.json();
        approvalId = approvalData.data.documentId;

        // Step 3: Link all items to the approval
        const updateResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.APPROVAL_REQUESTS.GET(approvalId)}?populate=itemrequests`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            data: {
              itemrequests: createdItemIds
            }
          })
        });

        if (!updateResponse.ok) {
          const errorData = await updateResponse.json();
          console.error('Update response error:', errorData);
          throw new Error(errorData.error?.message || 'Failed to link items to approval request');
        }

        const updatedApprovalData = await updateResponse.json();
        console.log('Updated approval request:', updatedApprovalData);

        console.log('Approval Request Submitted:', {
          approvalId,
          items,
          requestedBy: requestorName,
          createdAt: new Date().toISOString()
        });
        
        setSubmitSuccess(true);
        setIsSubmitting(false);

        // Reset form after 2 seconds
        setTimeout(() => {
          navigate('/budget-approval');
        }, 2000);
      } catch (error) {
        console.error('Submit error:', error);
        setIsSubmitting(false);
        
        // Cleanup: Delete all created items if approval fails
        if (createdItemIds.length > 0) {
          console.log('Cleaning up created items...');
          const deletePromises = createdItemIds.map(itemId =>
            fetch(`${API_BASE_URL}${API_ENDPOINTS.ITEM_REQUESTS.DELETE(itemId)}`, {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${token}`,
              }
            }).catch(err => console.error(`Failed to delete item ${itemId}:`, err))
          );
          await Promise.all(deletePromises);
          console.log('Items cleanup completed');
        }

        // Cleanup: Delete approval request if it was created
        if (approvalId) {
          console.log('Cleaning up created approval request...');
          fetch(`${API_BASE_URL}${API_ENDPOINTS.APPROVAL_REQUESTS.DELETE(approvalId)}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
            }
          }).catch(err => console.error('Failed to delete approval request:', err));
        }

        setSubmitError(error instanceof Error ? error.message : 'Failed to submit approval request');
      }
    } catch (error) {
      console.error('Outer error:', error);
      setSubmitError(error instanceof Error ? error.message : 'Failed to submit approval request');
    }
  };

  const totalRequestAmount = items.reduce((sum, item) => sum + item.totalAmount, 0);

  return (
    <Box>
      <Header />
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, sm: 2 } }}>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/budget-approval')}
          >
            Back to Dashboard
          </Button>
        </Box>

      {submitSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Approval request submitted successfully! Redirecting...
        </Alert>
      )}

      {submitError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {submitError}
        </Alert>
      )}

      <Paper sx={{ p: 4, mb: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 2, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
            Request Approval
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: '0.813rem', md: '0.875rem' } }}>
            Add items and submit for approval. Pre-approved allocations are shown as guidance.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <TextField
            fullWidth
            label="Title of Request*"
            value={requestTitle}
            onChange={(e) => setRequestTitle(e.target.value)}
            placeholder="e.g., Purchase of Construction Materials"
            sx={{ mb: 3, '& .MuiOutlinedInput-input': { fontSize: { xs: '0.875rem', md: '1rem' } } }}
          />
          <Typography variant="body1" sx={{ fontSize: { xs: '0.813rem', md: '0.875rem' } }}>
            You are: <strong>{requestorName} [{user?.username}]</strong>
          </Typography>
        </Box>

        {items.length > 0 && (
          <TableContainer sx={{ mb: 3, overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', md: '0.875rem' }, p: { xs: 0.75, md: 1 } }}>Item</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', md: '0.875rem' }, p: { xs: 0.75, md: 1 }, whiteSpace: 'nowrap' }}>
                    Qty
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', md: '0.875rem' }, p: { xs: 0.75, md: 1 }, whiteSpace: 'nowrap' }}>
                    Rate
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', md: '0.875rem' }, p: { xs: 0.75, md: 1 }, whiteSpace: 'nowrap' }}>
                    Total
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', md: '0.875rem' }, p: { xs: 0.75, md: 1 }, display: { xs: 'none', sm: 'table-cell' } }}>
                    Files
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', md: '0.875rem' }, p: { xs: 0.75, md: 1 } }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, p: { xs: 0.75, md: 1 } }}>{item.itemName}</TableCell>
                    <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, p: { xs: 0.75, md: 1 } }}>{item.quantity}</TableCell>
                    <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, p: { xs: 0.75, md: 1 } }}>₹{item.rate.toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', md: '0.875rem' }, p: { xs: 0.75, md: 1 } }}>
                      ₹{item.totalAmount.toFixed(2)}
                    </TableCell>
                    <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' }, p: { xs: 0.75, md: 1 } }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {item.files && item.files.length > 0 ? (
                          <Stack spacing={0.25}>
                            {item.files.map((file, idx) => (
                              <Chip
                                key={idx}
                                icon={<AttachFileIcon />}
                                label={`${file.name}`}
                                size="small"
                                variant="outlined"
                                title={`${file.name} (${formatFileSize(file.size)})`}
                                sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}
                              />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}>
                            No files
                          </Typography>
                        )}
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => handleManageItemFiles(index)}
                          sx={{ textTransform: 'none', fontSize: { xs: '0.65rem', md: '0.75rem' } }}
                        >
                          {item.files && item.files.length > 0 ? 'Edit' : 'Add'}
                        </Button>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ p: { xs: 0.5, md: 1 } }}>
                      <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'center' }}>
                        <Button
                          size="small"
                          color="primary"
                          startIcon={<EditIcon />}
                          onClick={() => handleEditItem(index)}
                          sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDeleteItem(index)}
                          sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingApprovalItemIndex(null);
              setSelectedItemEstimate(null);
              setFormData({
                itemName: '',
                quantity: '',
                rate: '',
                totalAmount: '',
                comment: '',
              });
              setUploadedFiles([]);
              setOpenDialog(true);
            }}
          >
            Add Item
          </Button>
        </Box>

        {items.length > 0 && (
          <Paper sx={{ p: { xs: 1.5, md: 3 }, backgroundColor: '#f9f9f9', mb: 3 }}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 'bold', fontSize: { xs: '0.813rem', md: '0.875rem' } }}>
                    Total Request Amount
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold', fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                    ₹{totalRequestAmount.toFixed(2)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        )}

        <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, justifyContent: 'flex-end', flexDirection: { xs: 'column', sm: 'row' } }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/budget-approval')}
            disabled={isSubmitting}
            fullWidth={true}
            sx={{ fontSize: { xs: '0.813rem', md: '0.875rem' } }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleSubmit}
            disabled={items.length === 0 || !requestorName || !requestTitle.trim() || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
            fullWidth={true}
            sx={{ fontSize: { xs: '0.813rem', md: '0.875rem' } }}
          >
            {isSubmitting ? 'Processing...' : 'Submit Request'}
          </Button>
        </Box>
      </Paper>

      {/* Upload Status Dialog */}
      <Dialog
        open={isSubmitting}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <HourglassBottomIcon sx={{ color: '#1976d2', animation: 'spin 2s linear infinite', fontSize: 30 }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Uploading Files...
              </Typography>
            </Box>

            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Please wait while your files are being uploaded. Do not close this window or navigate away.
            </Typography>

            {totalFilesToUpload > 0 && (
              <>
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Progress</strong>
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      <strong>{filesUploaded} / {totalFilesToUpload}</strong>
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={(filesUploaded / totalFilesToUpload) * 100}
                    sx={{ height: '8px', borderRadius: '4px' }}
                  />
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  File Status:
                </Typography>
                <Box sx={{ maxHeight: '250px', overflowY: 'auto', backgroundColor: '#fafafa', p: 2, borderRadius: 1 }}>
                  {Object.entries(uploadProgress).map(([fileName, status]) => (
                    <Box 
                      key={fileName}
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        p: 0.75,
                        borderBottom: '1px solid #eee',
                        '&:last-child': {
                          borderBottom: 'none'
                        }
                      }}
                    >
                      <Typography 
                        variant="caption" 
                        sx={{ wordBreak: 'break-word', flex: 1, mr: 1 }}
                        title={fileName}
                      >
                        {fileName.length > 30 ? `${fileName.substring(0, 27)}...` : fileName}
                      </Typography>
                      <Typography 
                        variant="caption"
                        sx={{ 
                          ml: 1,
                          whiteSpace: 'nowrap',
                          color: status.includes('✅') ? '#4caf50' : status.includes('❌') ? '#f44336' : '#1976d2',
                          fontWeight: 'bold'
                        }}
                      >
                        {status}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}

            {totalFilesToUpload === 0 && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CircularProgress size={40} sx={{ mb: 2 }} />
                <Typography variant="body2" color="textSecondary">
                  Creating approval request...
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Item Dialog */}
      <Dialog open={openDialog} onClose={() => {
        setOpenDialog(false);
        setEditingApprovalItemIndex(null);
        setFormData({
          itemName: '',
          quantity: '',
          rate: '',
          totalAmount: '',
          comment: '',
        });
        setUploadedFiles([]);
        setSelectedItemEstimate(null);
      }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>{editingApprovalItemIndex !== null ? 'Edit Item' : 'Add Item for Approval'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Grid container spacing={1} alignItems="flex-start">
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={itemEstimates}
                  getOptionLabel={(option) => option.item}
                  value={selectedItemEstimate}
                  onChange={(_, newValue) => {
                    setSelectedItemEstimate(newValue);
                    if (newValue) {
                      setFormData({
                        ...formData,
                        itemName: newValue.item,
                      });
                    }
                  }}
                  fullWidth
                  renderInput={(params) => (
                    <TextField {...params} label="Select Item" placeholder="Search..." sx={{ '& .MuiOutlinedInput-input': { fontSize: { xs: '0.875rem', md: '1rem' } } }} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={1} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '56px' }}>
                <Typography variant="body2" sx={{ fontWeight: '500', color: '#999', fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                  Or
                </Typography>
              </Grid>
              <Grid item xs={12} sm={5}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenNewItemDialog(true)}
                  fullWidth
                  sx={{ height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                >
                  Add New
                </Button>
              </Grid>
            </Grid>

            {selectedItemEstimate && (
              <Box sx={{ p: { xs: 1.5, md: 2 }, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '0.813rem', md: '0.875rem' } }}>
                  Pre Estimated Allocation:
                </Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>Item: {selectedItemEstimate.item}</Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>Unit: {selectedItemEstimate.unit}</Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>Quantity: {selectedItemEstimate.quantity}</Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>Unit Cost: ₹{selectedItemEstimate.unitCost.toFixed(2)}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                  Total Cost: ₹{selectedItemEstimate.totalCost.toFixed(2)}
                </Typography>
                {selectedItemEstimate.remarks && (
                  <Typography variant="body2" sx={{ mt: 1, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>Remarks: {selectedItemEstimate.remarks}</Typography>
                )}
              </Box>
            )}

            <TextField
              fullWidth
              label="Requested Quantity"
              type="number"
              value={formData.quantity}
              onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
              placeholder="e.g., 50"
              inputProps={{ step: '0.01' }}
              sx={{
                '& .MuiOutlinedInput-input': { fontSize: { xs: '0.875rem', md: '1rem' } },
                '& .MuiOutlinedInput-root': {
                  borderColor: selectedItemEstimate && formData.quantity
                    ? parseFloat(formData.quantity) < selectedItemEstimate.quantity
                      ? '#4caf50'
                      : parseFloat(formData.quantity) > selectedItemEstimate.quantity
                      ? '#ff9800'
                      : 'inherit'
                    : 'inherit',
                  '& fieldset': {
                    borderColor: selectedItemEstimate && formData.quantity
                      ? parseFloat(formData.quantity) < selectedItemEstimate.quantity
                        ? '#4caf50'
                        : parseFloat(formData.quantity) > selectedItemEstimate.quantity
                        ? '#ff9800'
                        : 'inherit'
                      : 'inherit',
                  },
                  '&:hover fieldset': {
                    borderColor: selectedItemEstimate && formData.quantity
                      ? parseFloat(formData.quantity) < selectedItemEstimate.quantity
                        ? '#4caf50'
                        : parseFloat(formData.quantity) > selectedItemEstimate.quantity
                        ? '#ff9800'
                        : 'inherit'
                      : 'inherit',
                  },
                },
              }}
            />
            <TextField
              fullWidth
              label="Requested Rate per Unit"
              type="number"
              value={formData.rate}
              onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
              placeholder="e.g., 100"
              inputProps={{ step: '0.01' }}
              InputProps={{ startAdornment: '₹' }}
              sx={{
                '& .MuiOutlinedInput-input': { fontSize: { xs: '0.875rem', md: '1rem' } },
                '& .MuiOutlinedInput-root': {
                  borderColor: selectedItemEstimate && formData.rate
                    ? parseFloat(formData.rate) < selectedItemEstimate.unitCost
                      ? '#4caf50'
                      : parseFloat(formData.rate) > selectedItemEstimate.unitCost
                      ? '#ff9800'
                      : 'inherit'
                    : 'inherit',
                  '& fieldset': {
                    borderColor: selectedItemEstimate && formData.rate
                      ? parseFloat(formData.rate) < selectedItemEstimate.unitCost
                        ? '#4caf50'
                        : parseFloat(formData.rate) > selectedItemEstimate.unitCost
                        ? '#ff9800'
                        : 'inherit'
                      : 'inherit',
                  },
                  '&:hover fieldset': {
                    borderColor: selectedItemEstimate && formData.rate
                      ? parseFloat(formData.rate) < selectedItemEstimate.unitCost
                        ? '#4caf50'
                        : parseFloat(formData.rate) > selectedItemEstimate.unitCost
                        ? '#ff9800'
                        : 'inherit'
                      : 'inherit',
                  },
                },
              }}
            />
            <TextField
              fullWidth
              label="Total Amount"
              type="number"
              value={formData.totalAmount}
              onChange={(e) => setFormData({ ...formData, totalAmount: e.target.value })}
              placeholder="Enter manually"
              inputProps={{ step: '0.01' }}
              InputProps={{ startAdornment: '₹' }}
              sx={{
                '& .MuiOutlinedInput-input': { fontSize: { xs: '0.875rem', md: '1rem' } },
                '& .MuiOutlinedInput-root': {
                  borderColor: selectedItemEstimate && formData.totalAmount
                    ? parseFloat(formData.totalAmount) < selectedItemEstimate.totalCost
                      ? '#4caf50'
                      : parseFloat(formData.totalAmount) > selectedItemEstimate.totalCost
                      ? '#ff9800'
                      : 'inherit'
                    : 'inherit',
                  '& fieldset': {
                    borderColor: selectedItemEstimate && formData.totalAmount
                      ? parseFloat(formData.totalAmount) < selectedItemEstimate.totalCost
                        ? '#4caf50'
                        : parseFloat(formData.totalAmount) > selectedItemEstimate.totalCost
                        ? '#ff9800'
                        : 'inherit'
                      : 'inherit',
                  },
                  '&:hover fieldset': {
                    borderColor: selectedItemEstimate && formData.totalAmount
                      ? parseFloat(formData.totalAmount) < selectedItemEstimate.totalCost
                        ? '#4caf50'
                        : parseFloat(formData.totalAmount) > selectedItemEstimate.totalCost
                        ? '#ff9800'
                        : 'inherit'
                      : 'inherit',
                  },
                },
              }}
            />

            <TextField
              fullWidth
              label="Comment (Optional)"
              value={formData.comment}
              onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
              placeholder="Add notes or instructions"
              multiline
              rows={3}
              sx={{ '& .MuiOutlinedInput-input': { fontSize: { xs: '0.875rem', md: '1rem' } } }}
            />

            {/* File Upload Section */}
            <Box sx={{ borderTop: '1px solid #ddd', pt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '0.813rem', md: '0.875rem' } }}>
                Attach Files/Artifacts (Optional)
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                sx={{ mb: 2, fontSize: { xs: '0.75rem', md: '0.875rem' } }}
              >
                Upload Files
                <input
                  type="file"
                  hidden
                  multiple
                  onChange={handleFileUpload}
                />
              </Button>

              {uploadedFiles.length > 0 && (
                <Box sx={{ backgroundColor: '#f9f9f9', p: { xs: 1, md: 2 }, borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '0.813rem', md: '0.875rem' } }}>
                    Uploaded Files ({uploadedFiles.length})
                  </Typography>
                  <Stack spacing={0.75}>
                    {uploadedFiles.map((file, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          p: { xs: 0.75, md: 1 },
                          backgroundColor: '#fff',
                          borderRadius: 1,
                          border: '1px solid #e0e0e0',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <AttachFileIcon sx={{ fontSize: { xs: '16px', md: '20px' }, color: '#1976d2' }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: '500', fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                              {file.name.length > 20 ? `${file.name.substring(0, 17)}...` : file.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}>
                              {formatFileSize(file.size)}
                            </Typography>
                          </Box>
                        </Box>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => handleRemoveFile(idx)}
                          sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}
                        >
                          Remove
                        </Button>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenDialog(false);
            setEditingApprovalItemIndex(null);
            setFormData({
              itemName: '',
              quantity: '',
              rate: '',
              totalAmount: '',
              comment: '',
            });
            setUploadedFiles([]);
            setSelectedItemEstimate(null);
          }}>Cancel</Button>
          <Button onClick={handleAddItem} variant="contained" color="primary">
            {editingApprovalItemIndex !== null ? 'Update Item' : 'Add Item'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Item to Estimates (BOM) Dialog */}
      <Dialog open={openNewItemDialog} onClose={() => setOpenNewItemDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>Add New Item</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Item Name*"
              value={newItemData.item}
              onChange={(e) => setNewItemData({ ...newItemData, item: e.target.value })}
              placeholder="e.g., Office Chair"
              sx={{ '& .MuiOutlinedInput-input': { fontSize: { xs: '0.875rem', md: '1rem' } } }}
            />
            <TextField
              fullWidth
              label="Unit*"
              value={newItemData.unit}
              onChange={(e) => setNewItemData({ ...newItemData, unit: e.target.value })}
              placeholder="e.g., pieces, kg, meters"
              sx={{ '& .MuiOutlinedInput-input': { fontSize: { xs: '0.875rem', md: '1rem' } } }}
            />
            <TextField
              fullWidth
              label="Remarks"
              value={newItemData.remarks}
              onChange={(e) => setNewItemData({ ...newItemData, remarks: e.target.value })}
              placeholder="e.g., Color: Black, Material: Leather"
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-input': { fontSize: { xs: '0.875rem', md: '1rem' } } }}
            />
            <TextField
              fullWidth
              label="Quantity*"
              type="number"
              value={newItemData.quantity}
              onChange={(e) => setNewItemData({ ...newItemData, quantity: e.target.value })}
              placeholder="e.g., 50"
              inputProps={{ step: '0.01' }}
              sx={{ '& .MuiOutlinedInput-input': { fontSize: { xs: '0.875rem', md: '1rem' } } }}
            />
            <TextField
              fullWidth
              label="Unit Cost*"
              type="number"
              value={newItemData.unitCost}
              onChange={(e) => {
                const unitCost = e.target.value;
                const quantity = parseFloat(newItemData.quantity) || 0;
                const totalCost = quantity * parseFloat(unitCost || '0');
                setNewItemData({ 
                  ...newItemData, 
                  unitCost,
                  totalCost: totalCost.toString()
                });
              }}
              placeholder="e.g., 100"
              inputProps={{ step: '0.01' }}
              InputProps={{ startAdornment: '₹' }}
              sx={{ '& .MuiOutlinedInput-input': { fontSize: { xs: '0.875rem', md: '1rem' } } }}
            />
            <TextField
              fullWidth
              label="Total Cost*"
              type="number"
              value={newItemData.totalCost}
              onChange={(e) => setNewItemData({ ...newItemData, totalCost: e.target.value })}
              placeholder="Enter or auto-calculated"
              inputProps={{ step: '0.01' }}
              InputProps={{ startAdornment: '₹' }}
              helperText={
                newItemData.quantity && newItemData.unitCost
                  ? `Calculated: ₹${(parseFloat(newItemData.quantity) * parseFloat(newItemData.unitCost)).toFixed(2)} (editable)`
                  : 'Enter quantity and unit cost to auto-calculate'
              }
              sx={{ '& .MuiOutlinedInput-input': { fontSize: { xs: '0.875rem', md: '1rem' } } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenNewItemDialog(false);
            setNewItemData({
              item: '',
              unit: '',
              remarks: '',
              quantity: '',
              unitCost: '',
              totalCost: '',
            });
          }}>Cancel</Button>
          <Button onClick={handleAddNewItem} variant="contained" color="primary">
            Create & Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Item Files Dialog */}
      <Dialog open={openFileManagerDialog} onClose={() => {
        setOpenFileManagerDialog(false);
        setEditingItemIndex(null);
        setUploadedFiles([]);
      }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: { xs: '0.95rem', md: '1.25rem' } }}>
          Manage Files: {editingItemIndex !== null && items[editingItemIndex].itemName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
              Upload files to attach to this item
            </Typography>
            
            <Button
              fullWidth
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
              sx={{ mb: 1, fontSize: { xs: '0.75rem', md: '0.875rem' } }}
            >
              Upload Files
              <input
                type="file"
                hidden
                multiple
                onChange={handleFileUpload}
              />
            </Button>

            {uploadedFiles.length > 0 && (
              <Box sx={{ backgroundColor: '#f9f9f9', p: { xs: 1, md: 2 }, borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '0.813rem', md: '0.875rem' } }}>
                  Files for this Item ({uploadedFiles.length})
                </Typography>
                <Stack spacing={0.75}>
                  {uploadedFiles.map((file, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: { xs: 0.75, md: 1 },
                        backgroundColor: '#fff',
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <AttachFileIcon sx={{ fontSize: { xs: '16px', md: '20px' }, color: '#1976d2' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: '500', fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                            {file.name.length > 20 ? `${file.name.substring(0, 17)}...` : file.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary\" sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}>
                            {formatFileSize(file.size)}
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleRemoveFile(idx)}
                        sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}
                      >
                        Remove
                      </Button>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenFileManagerDialog(false);
            setEditingItemIndex(null);
            setUploadedFiles([]);
          }}>Cancel</Button>
          <Button onClick={handleSaveItemFiles} variant="contained" color="primary">
            Save Files
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </Box>
  );
};

